import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertService } from '../../_alert';
import { NgxSpinnerService } from 'ngx-spinner';
import { DepartmentService } from '../../services/jrni/department.service';
import { DatastoreService } from '../../services/datastore.service';
import { ServicesService } from '../../services/jrni/services.service';
import { TranslateService } from '@ngx-translate/core';
import { BasketService } from '../../services/jrni/basket.service';
import { ApiService } from '../../services/jrni/api.service';
import { AvailabilityService } from '../../services/jrni/availability.service';
import { CivicaService } from '../../services/civica.service';
import { Subscription, timer } from 'rxjs';
import moment from 'moment-timezone';
import { AppConfig } from '../../app.config';
import { SOLIHULL_CITIZENSHIP_SERVICE_IDS } from '../../services/solihull-terms';

/**
 * Civica eStore payment return leg - PRE-BASKET (book-after-payment).
 *
 * The gateway returns to `payment-verify/:company/:basket/:serviceItems`. No
 * JRNI booking exists yet: bl-civica-api stored the booking intent and, once
 * Civica confirms payment, forwards the notification to the n8n
 * "Civica Post-Basket Verify" workflow, which checks out the JRNI basket, saves
 * the schema answers as booking metadata, links the extra items, sets the
 * booking price, pays the purchase, marks the row PAID and pushes the case to
 * Oracle CRM. This page polls /payment/civica/pre-basket/find until the row
 * carries the purchase (data.purchase_id / status PAID), runs the ceremony
 * paperwork the worker does not own (front-sheet, admin and welcome emails,
 * the payment note), then routes to confirmation.
 *
 * Failure and cancel handling (carried over from the Gen-1 Solihull app):
 *   - Civica also puts its own outcome on the querystring. A SUCCESS code is
 *     ignored (the out-of-band notification is the only trustworthy success
 *     signal) but a cancel / decline never produces an Accepted notification,
 *     so those codes are honoured: the row is stamped and the customer goes to
 *     estore-fail, where they can retry the same Civica basket or restart.
 *   - A pre-payment back-out via the eStore "back to site" button lands on
 *     `/estore-fail` directly (civica.service sets back_url), so it never
 *     starts a ten-minute poll on a payment that was never made.
 *   - `?cancelled=true` (the Gen-2 family convention) is also accepted and
 *     restarts the journey after clearing the abandoned basket.
 */
@Component({
  selector: 'app-verify-payment',
  templateUrl: './verify-payment.component.html',
  styleUrls: ['./verify-payment.component.scss'],
})
export class VerifyPaymentComponent implements OnInit, OnDestroy {
  // Civica eStore ResponseCodes on the browser return leg.
  //   00000 payment succeeded · 00001 payment failed
  //   00002 cancelled by the user · 99999 gateway error
  static readonly RESPONSE_CANCELLED = '00002';
  // Codes that mean no money was taken, so there is nothing to wait for.
  static readonly ABANDONED_RESPONSE_CODES = ['00001', '00002', '99999'];

  // Bound through the `| translate` pipe in the template, so these hold keys
  // rather than resolved strings - the gateway sends the customer back on a
  // cold page load, where translateService.instant() would beat the i18n fetch
  // and render the raw key.
  pageTitle = 'PAYMENT.TITLE';
  verifyPageText = 'PAYMENT.WAIT';
  paymentFailed = false;
  maxRetriesReached = false;

  companyId: any = null;
  basket: string = null;
  serviceItem: string = null;

  company: any;
  parent: any;
  member: any;
  config: any;
  selectedLocation: any;
  selectedService: any;
  selectedSlot: any;

  private pollSub: Subscription;
  // 2s interval; 300 attempts is about 10 minutes, long enough to cover a slow
  // Civica notification plus the worker's JRNI checkout.
  private maxRetries = 300;
  private findErrorCount = 0;
  private maxFindErrors = 10;
  private initialDataExtracted = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private departmentService: DepartmentService,
    private alertService: AlertService,
    private spinner: NgxSpinnerService,
    private dataStore: DatastoreService,
    private translateService: TranslateService,
    private servicesService: ServicesService,
    private basketService: BasketService,
    private apiService: ApiService,
    private availabilityService: AvailabilityService,
    private civicaService: CivicaService
  ) {}

  async ngOnInit() {
    this.spinner.show();

    try {
      this.config = await this.apiService.getConfig(AppConfig.settings.core.company_id);
      this.dataStore.config = this.config;
      const retries = this.config?.journey_config?.ceremonies?.payment_verify_retries;
      if (retries !== undefined && !isNaN(parseInt(retries, 10))) {
        this.maxRetries = parseInt(retries, 10);
      }
    } catch (err) {
      console.error('Error loading config:', err);
    }

    this.route.params.subscribe(async (params) => {
      if (params.company) {
        this.companyId = params.company;
      }
      if (params.basket) {
        this.basket = params.basket;
      }
      if (params.serviceItems) {
        const items = `${params.serviceItems}`.split(',').filter((s) => s);
        if (items.length > 0) {
          // One service item == one booking; the ceremonies journey only ever
          // sends a single line.
          this.serviceItem = items[0];
        }
      }

      if (!this.basket || !this.serviceItem) {
        console.error('Missing basket/service item on the verify route');
        this.spinner.hide();
        this.paymentFailed = true;
        this.pageTitle = 'PAYMENT.ERROR_TITLE';
        this.verifyPageText = 'PAYMENT.ERROR';
        return;
      }

      if (this.route.snapshot.queryParams['cancelled']) {
        await this.restartJourneyAfterCancel();
        return;
      }

      await this.initPreBasket();
    });
  }

  ngOnDestroy() {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
  }

  // ---- Civica pre-basket (book-after-payment) ----

  private async resolveLocation() {
    try {
      const parent = await this.departmentService.getParentDepartment();
      this.parent = parent;
      this.company = parent;
      this.selectedLocation = parent;
      await this.departmentService.setLocation(parent);

      try {
        const children = await this.departmentService.getChildDepartments(parent);
        for (const location of children) {
          if (location.id == this.companyId) {
            this.company = location;
            this.selectedLocation = location;
            await this.departmentService.setLocation(location);
            break;
          }
        }
      } catch (err) {
        console.error('Error resolving child location:', err);
      }
    } catch (err) {
      console.error('Error resolving location:', err);
    }
  }

  private async initPreBasket() {
    const params = this.route.snapshot.queryParams;
    console.log('Verify payment (pre-basket)', {
      company: this.companyId,
      basket: this.basket,
      serviceItem: this.serviceItem,
      responseCode: params['ResponseCode'],
      responseDescription: params['ResponseDescription'],
    });

    await this.resolveLocation();

    // A cancel or a decline never produces an Accepted notification, so the
    // querystring is the ONLY signal we will ever get for those: without acting
    // on it this page polls a row that stays NEW until the timeout, telling a
    // customer who deliberately backed out that their payment could not be
    // verified. A success code is deliberately NOT trusted (CallingAppTxnRef
    // comes back empty) - the notification is the only success signal.
    const responseCode = `${params['ResponseCode'] || ''}`;
    if (VerifyPaymentComponent.ABANDONED_RESPONSE_CODES.indexOf(responseCode) !== -1) {
      await this.handleGatewayAbandon(responseCode, params);
      return;
    }

    this.startFindPolling();
  }

  /**
   * Civica told the browser the payment did not happen (cancelled or declined).
   *
   * Trust that only if the stored row agrees - if money was in fact taken and
   * the worker has already run, honour the booking rather than stranding a paid
   * customer behind a failure page. Otherwise stamp the row so it does not sit
   * at NEW forever and send them to estore-fail.
   */
  private async handleGatewayAbandon(responseCode: string, params: any) {
    const cancelled = responseCode === VerifyPaymentComponent.RESPONSE_CANCELLED;
    let found: any = null;

    try {
      found = await this.civicaService.preBasketFind(this.companyId, this.basket, this.serviceItem);
      console.log(`preBasketFind before honouring ${responseCode}`, found);

      if (found?.data?.purchase_id || `${found?.status}` === 'PAID') {
        console.warn(
          `Gateway returned ${responseCode} but the pre-basket row is already paid - completing the booking`
        );
        await this.extractInitialData(found);
        this.initialDataExtracted = true;
        await this.completeBooking(found);
        return;
      }

      // Payment was confirmed out of band but the booking is still being built.
      // Let the worker finish rather than marking the row abandoned under it.
      if (found?.data?.payment_notification?.request_status === 'Accepted') {
        console.warn(
          `Gateway returned ${responseCode} but Civica notified Accepted - polling for the booking instead`
        );
        this.startFindPolling();
        return;
      }
    } catch (err) {
      console.error('Could not read the pre-basket row before marking it abandoned:', err);
    }

    // Best effort. The worker only guards on PAID, so a genuine late Accepted
    // notification would still create the booking after this.
    if (found?.ref) {
      try {
        await this.civicaService.preBasketUpdate(
          this.companyId,
          this.basket,
          this.serviceItem,
          cancelled ? 'CANCELLED' : 'FAILED_GATEWAY',
          found.ref,
          {
            gateway_response: {
              response_code: responseCode,
              response_description: params['ResponseDescription'] || '',
              basket_ref: params['BasketRef'] || '',
              returned_at: moment().tz('Europe/London').format('YYYY-MM-DD HH:mm:ss'),
            },
          }
        );
      } catch (err) {
        console.error(`Failed to mark pre-basket row as ${cancelled ? 'CANCELLED' : 'FAILED_GATEWAY'}:`, err);
      }
    }

    try {
      this.apiService.logPaymentExtra(
        { id: this.companyId },
        { id: found?.data?.service_id },
        found?.data?.purchase_id,
        {
          basket: this.basket,
          service_item: this.serviceItem,
          pre_basket_status: found?.status,
          gateway_response_code: responseCode,
          gateway_response_description: params['ResponseDescription'],
          basket_ref: params['BasketRef'],
        }
      );
    } catch (err) {
      console.error(err);
    }

    localStorage.setItem(
      'estoreError',
      JSON.stringify(
        params['ResponseDescription'] ||
          (cancelled
            ? 'Your payment was cancelled and your booking has not been confirmed'
            : 'Your payment has not been successful')
      )
    );
    this.spinner.hide();
    this.router.navigate(['estore-fail']);
  }

  private startFindPolling() {
    // Poll immediately, then every 2 seconds until the worker has created the
    // JRNI booking (or reported a failure).
    this.pollSub = timer(0, 2000).subscribe(async (attempt) => {
      if (attempt >= this.maxRetries) {
        this.pollSub.unsubscribe();
        this.spinner.hide();
        this.maxRetriesReached = true;
        this.pageTitle = 'PAYMENT.TIMEOUT_TITLE';
        this.verifyPageText = 'PAYMENT.TIMEOUT';
        return;
      }

      try {
        const found: any = await this.civicaService.preBasketFind(this.companyId, this.basket, this.serviceItem);
        console.log(`preBasketFind (attempt ${attempt + 1})`, found);

        if (!found || !found.data) {
          return;
        }

        if (!this.initialDataExtracted) {
          await this.extractInitialData(found);
          this.initialDataExtracted = true;
        }

        const status = `${found.status || ''}`;

        if (status.indexOf('FAILED') !== -1 || status === 'CANCELLED') {
          this.pollSub.unsubscribe();
          this.handlePreBasketFailure(found);
          return;
        }

        // purchase_id is written once the worker has checked the basket out.
        if (found.data.purchase_id || status === 'PAID') {
          this.pollSub.unsubscribe();
          await this.completeBooking(found);
          return;
        }

        // Civica has confirmed payment but the booking is not built yet.
        if (found.data.payment_notification) {
          this.verifyPageText = 'PAYMENT.RECEIVED';
        }
      } catch (err) {
        console.error('Error in preBasketFind polling:', err);
        this.findErrorCount++;
        if (this.findErrorCount >= this.maxFindErrors) {
          this.pollSub.unsubscribe();
          this.spinner.hide();
          this.paymentFailed = true;
          this.pageTitle = 'PAYMENT.ERROR_TITLE';
          this.verifyPageText = 'PAYMENT.ERROR';
        }
      }
    });
  }

  /**
   * Rehydrate the journey state the confirmation page needs from the stored
   * pre-basket row - the browser may well be a fresh tab coming back from the
   * gateway, so nothing can be assumed to survive in memory.
   */
  private async extractInitialData(found: any) {
    const data = found.data;
    if (!data) {
      return;
    }

    if (data.auth_token) {
      localStorage.setItem('auth_token', data.auth_token);
    }

    if (data.journey_route) {
      this.dataStore.journeyRoute = data.journey_route;
    }

    if (data.slot) {
      this.selectedSlot = data.slot;
      this.dataStore.selectedSlot = data.slot;
      try {
        await this.availabilityService.setSelectedSlot(data.slot);
      } catch (err) {
        console.error('Error restoring slot:', err);
      }
    }

    this.dataStore.extraDuration = data.extra_duration || 0;

    if (data.extra_items?.fees) {
      this.dataStore.serviceFees = data.extra_items.fees;
    }
    if (data.extra_items?.lookupResponses) {
      this.dataStore.lookupResponses = data.extra_items.lookupResponses;
    }
    if (data.extra_items?.selectedAddress) {
      this.dataStore.selectedAddress = data.extra_items.selectedAddress;
    }
    if (data.extra_items?.resource) {
      localStorage.setItem('resource', JSON.stringify(data.extra_items.resource));
    }
    if (data.extra_items?.amount_due_now !== undefined) {
      localStorage.setItem('totalCost', JSON.stringify(data.extra_items.amount_due_now));
    }
  }

  /**
   * The worker owns the money: checkout, extra-item links, booking price and
   * the purchase payment are all done server-side by the time we get here.
   * What is left is the ceremonies paperwork the worker does not do.
   */
  private async completeBooking(found: any) {
    try {
      const data = found.data;

      const purchase: any = await this.apiService.findPurchase({ id: this.companyId }, data.purchase_id);
      const booking = purchase._embedded.bookings[0];
      this.member = purchase._embedded.client;
      this.dataStore.booking = booking;
      this.dataStore.purchase = purchase;

      const service: any = await this.apiService.getService({ id: booking.company_id }, booking.service_id);
      this.selectedService = service;
      this.dataStore.selectedService = service;
      await this.servicesService.set(service);

      if (!this.selectedSlot) {
        this.selectedSlot = { start: booking.datetime };
        this.dataStore.selectedSlot = this.selectedSlot;
        await this.availabilityService.setSelectedSlot(this.selectedSlot);
      }

      localStorage.setItem('booking', JSON.stringify(booking));
      localStorage.setItem('date', JSON.stringify(this.selectedSlot));
      localStorage.setItem('location', JSON.stringify(this.selectedLocation));
      localStorage.setItem('service', JSON.stringify(service));

      await this.runPostPaymentSideEffects(found, booking);

      this.spinner.hide();
      this.router.navigate(['confirmation']);
    } catch (err) {
      console.error('Error completing booking after payment:', err);
      this.spinner.hide();
      this.paymentFailed = true;
      this.pageTitle = 'PAYMENT.ERROR_TITLE';
      this.verifyPageText = 'PAYMENT.ERROR';
    }
  }

  /** The base ceremony fee line, exactly as the n8n worker's isBase() sees it. */
  private isBaseFee(fee: any): boolean {
    return fee?.questionid === undefined || fee?.questionid === null || fee?.questionid === 'noid2';
  }

  /**
   * Emails and the payment note. Guarded on the pre-basket line so a refresh of
   * the return URL cannot send the customer a second front sheet. The Oracle
   * CRM push is NOT done here: the n8n worker pushes the case (gated on
   * journey_config.crm_push === 'osc' and idempotent on data.crm_pushed).
   */
  private async runPostPaymentSideEffects(found: any, booking: any) {
    const guardKey = `preBasketCompleted:${this.basket}:${this.serviceItem}`;
    if (localStorage.getItem(guardKey)) {
      console.log('Post-payment side effects already run for this booking');
      return;
    }
    localStorage.setItem(guardKey, `${booking.id}`);

    const data = found.data || {};
    const fees: any[] = Array.isArray(data.extra_items?.fees) ? data.extra_items.fees : [];
    const hasDeposit = !!data.extra_items?.deposit_payment;
    // Everything other than the base ceremony fee is charged in full - that is
    // the "& Certificates in Full" half of the note the back office reads.
    const certs = fees
      .filter((fee) => !this.isBaseFee(fee))
      .reduce((sum, fee) => sum + (Number(fee.quantity) > 0 ? Number(fee.quantity) : 1), 0);
    const amountPaid = data.extra_items?.amount_due_now;
    const receipt = data.payment_notification?.basket_reference || data.basket_reference || '';

    let note = 'Payment type: ';
    note += hasDeposit ? 'Ceremony Fee Deposit' : 'Ceremony Fee';
    if (certs > 0) {
      note += ' & Certificates in Full';
    }
    note += ` | Deposit? ${hasDeposit ? 'Yes' : 'No'}`;
    if (certs > 0) {
      note += ` | Number of certificates: ${certs}`;
    }
    note += ` | Deposit payment date: ${moment().tz('Europe/London').format('dddd, MMMM Do YYYY')}`;
    if (amountPaid !== undefined) {
      note += ` | Deposit payment amount: £${amountPaid}`;
    }
    if (receipt) {
      note += ` | Deposit receipt number: ${receipt}`;
    }

    try {
      await this.apiService.sendFrontSheetEmail(booking);
    } catch (err) {
      console.error('Error sending front sheet email:', err);
    }

    try {
      await this.apiService.addNoteToBooking({ id: booking.company_id }, booking.id, note);
    } catch (err) {
      console.error('Error adding payment note:', err);
    }

    try {
      const citizenship = SOLIHULL_CITIZENSHIP_SERVICE_IDS.includes(Number(booking.service_id));
      if (this.member?.id && !citizenship) {
        await this.apiService.sendWelcomeEmail(booking.company_id, this.member.id);
      }
    } catch (err) {
      console.error('Error sending welcome email:', err);
    }

    try {
      const inbox = this.company?.extra?.ceremonies_admin_inbox || this.parent?.extra?.ceremonies_admin_inbox;
      if (inbox) {
        await this.apiService.sendAdminEmail(booking, inbox);
      } else {
        console.warn('No ceremonies_admin_inbox configured; skipping admin email');
      }
    } catch (err) {
      console.error('Error sending admin email:', err);
    }
  }

  private handlePreBasketFailure(found: any) {
    console.error('Pre-basket reported failure', found);
    try {
      this.apiService.logPaymentExtra(
        { id: this.companyId },
        { id: found?.data?.service_id },
        found?.data?.purchase_id,
        {
          basket: this.basket,
          service_item: this.serviceItem,
          pre_basket_status: found?.status,
          payment_notification: found?.data?.payment_notification,
        }
      );
    } catch (err) {
      console.error(err);
    }

    localStorage.setItem('estoreError', JSON.stringify('Your payment has not been successful'));
    this.spinner.hide();
    this.router.navigate(['estore-fail']);
  }

  // The user abandoned the payment before paying (?cancelled=true): clear the
  // basket they abandoned and restart the journey on the landing page.
  async restartJourneyAfterCancel() {
    try {
      await this.apiService.deleteBaskets({ id: this.companyId });
    } catch (err) {
      console.warn('Could not clear basket on payment cancel/back:', err);
    }
    this.basketService.currentBasket = null;
    const journey = sessionStorage.getItem('journey');
    window.location.assign(journey ? `/start/${journey}` : `/start`);
  }
}
